import React, { useState, useEffect } from 'react';
import {
  Typography,
  ButtonPro,
  InputPro,
  TextAreaProControlled
} from 'components';
import './EditDesignationModal.scss';
import { useAppDispatch } from 'store';
import { updateDesignation } from 'store/MasterData';

interface Designation {
  id: string;
  name: string;
  description: string;
  createdDate: string;
  modifiedDate: string;
  createdBy: string;
}

interface EditDesignationModalProps {
  isOpen: boolean;
  designation: Designation | null;
  onClose: () => void;
  onSave: (designation: Designation) => void;
}

export const EditDesignationModal: React.FC<EditDesignationModalProps> = ({
  isOpen,
  designation,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Designation>({
    id: '',
    name: '',
    description: '',
    createdDate: '',
    modifiedDate: '',
    createdBy: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (designation) {
      setFormData({
        ...designation
      });
    }
  }, [designation]);

  const handleInputChange = (field: keyof Designation, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Designation name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Designation description is required';
    }

    if (formData.name.trim().length < 2) {
      newErrors.name = 'Designation name must be at least 2 characters';
    }

    if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      dispatch(
        updateDesignation({
          id: formData.id,
          name: formData.name,
          description: formData.description
        })
      );
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      createdDate: '',
      modifiedDate: '',
      createdBy: ''
    });
    setErrors({});
    onClose();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !designation) return null;

  return (
    <div className="modal-overlay">
      <div className="edit-designation-modal">
        <div className="modal-header">
          <Typography
            text="Edit Designation"
            type="heading3"
            weight="bold"
            className="modal-title"
          />
          <ButtonPro
            label="×"
            type="icon"
            size="small"
            onClick={handleClose}
            buttonClassNames="close-button"
          />
        </div>

        <div className="modal-content">
          <div className="form-grid">
            <div className="form-group">
              <InputPro
                name="designationName"
                id="designationName"
                label="Designation Name"
                required={true}
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                inputClass={errors.name ? 'error' : ''}
                placeholder="Enter designation name"
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>

            <div className="form-group full-width">
              <TextAreaProControlled
                label="Description"
                name="designationDescription"
                id="designationDescription"
                required={true}
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                inputClass={errors.description ? 'error' : ''}
                placeholder="Enter detailed description of this designation"
              />
              {errors.description && (
                <span className="error-message">{errors.description}</span>
              )}
            </div>

            <div className="form-group readonly-group">
              <InputPro
                name="createdBy"
                id="createdBy"
                label="Created By"
                value={formData.createdBy}
                onChange={() => {}} // No-op for readonly
                disabled={true}
                inputClass="readonly-input"
              />
            </div>

            <div className="form-group readonly-group">
              <InputPro
                name="createdDate"
                id="createdDate"
                label="Created Date"
                value={formatDate(formData.createdDate)}
                onChange={() => {}} // No-op for readonly
                disabled={true}
                inputClass="readonly-input"
              />
            </div>

            <div className="form-group readonly-group">
              <InputPro
                name="modifiedDate"
                id="modifiedDate"
                label="Last Modified"
                value={formatDate(formData.modifiedDate)}
                onChange={() => {}} // No-op for readonly
                disabled={true}
                inputClass="readonly-input"
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <ButtonPro
            label="Cancel"
            type="white"
            size="base"
            onClick={handleClose}
          />
          <ButtonPro
            label="Save Changes"
            type="primary"
            size="base"
            onClick={handleSave}
          />
        </div>
      </div>
    </div>
  );
};
